import React, { FC, useMemo, useState } from 'react';

import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import HeadphonesIcon from '@mui/icons-material/Headphones';
import SlideshowRoundedIcon from '@mui/icons-material/SlideshowRounded';
import { CoreUtils, ShortCodeEnums, GameClock, GameClockStyles, TimeZoneEnums } from '@nhl/nhl-breakaway-ui';

import CommonMessages from '~/core/commonMessages';
import { TicketsIcon } from '~/core/customIcons';
import { PageName } from '~/core/enums';
import { useOptionForLocale } from '~/core/hooks/useOptionForLocale';
import { showTicketsLink } from '~/core/utils';
import { defaultLocationData, useLocationQuery } from '~/redux/ducks/location';
import GameOrSeriesScoreboard from '~/views/components/gameOrSeriesScoreboard/GameOrSeriesScoreboard';
import { formatDate } from '~/views/components/gameOrSeriesScoreboard/utils';
import HowToWatchModal from '~/views/components/howToWatchModal/HowToWatchModal';
import TvBroadcast from '~/views/components/tvBroadcast/TvBroadcast';
import { useHowToWatchQuery } from '~/views/pages/dotcom/gameCenter/api';

import { CommonGCGame } from '../../types';

import RadioPlayer from './components/radioPlayer/RadioPlayer';
import SpecialEventDisplay from './components/specialEventDisplay/SpecialEventDisplay';

import {
    DateContainer,
    DateNetworkContainer,
    GameInfo,
    LinkContainer,
    LinksContainer,
    NetworkContainer,
    PeriodTimeContainer,
    ScoreboardButton,
    ScoreBox,
    ScoreContainer,
    SituationDescription,
} from './styles';

type ScoreboardProps = {
    game: CommonGCGame;
};

const Scoreboard: FC<ScoreboardProps> = ({ game }) => {
    const intl = useIntl();
    const { data: countryCode = defaultLocationData } = useLocationQuery({});

    const isLiveGame = CoreUtils.isGameLive(game.gameState);
    const postGame = CoreUtils.isPostGame(game.gameState);

    const { data: howToWatchData, isLoading: isHowToWatchDataLoading } = useHowToWatchQuery(game?.id);

    const ticketsLink = useOptionForLocale({
        [ShortCodeEnums.Language.English]: game.ticketsLink,
        [ShortCodeEnums.Language.French]: game.ticketsLinkFr,
    });

    const [isHowToWatchModalOpen, setIsHowToWatchModalOpen] = useState(false);
    const [isRadioPlayerOpen, setRadioPlayerOpen] = useState(false);

    const gameHasRadioStreams = useMemo<boolean>(
        () =>
            game.homeTeam.radioLink !== undefined ||
            game.homeTeam.radioLinkFr !== undefined ||
            game.awayTeam.radioLink !== undefined ||
            game.awayTeam.radioLinkFr !== undefined,
        [game]
    );

    // TODO - remove language requirement
    const isShowingHowToWatchButton =
        (CoreUtils.isGameActive(game.gameState) || CoreUtils.isPregame(game.gameState)) &&
        [ShortCodeEnums.Language.English, ShortCodeEnums.Language.French].includes(
            window.__SITE_SETTINGS__.languageCode as ShortCodeEnums.Language
        );
    const isShowingTicketsLink = showTicketsLink(game);
    const isShowingRadioListenButton = CoreUtils.isGameActive(game.gameState) && gameHasRadioStreams;
    const isShowingGameLinks = isShowingHowToWatchButton || isShowingTicketsLink || isShowingRadioListenButton;

    const gameLinks = (
        <LinksContainer>
            {isShowingHowToWatchButton && (
                <LinkContainer>
                    <ScoreboardButton onClick={() => setIsHowToWatchModalOpen(true)}>
                        <FormattedMessage {...CommonMessages.linkDescriptions.howToWatch} />
                        <SlideshowRoundedIcon />
                    </ScoreboardButton>
                    {isShowingTicketsLink && (
                        <ScoreboardButton href={ticketsLink}>
                            <FormattedMessage {...CommonMessages.linkDescriptions.tickets} />
                            <TicketsIcon />
                        </ScoreboardButton>
                    )}
                </LinkContainer>
            )}
            {isShowingRadioListenButton && (
                <LinkContainer>
                    <ScoreboardButton onClick={() => setRadioPlayerOpen(true)}>
                        <FormattedMessage {...CommonMessages.linkDescriptions.listen} />
                        <HeadphonesIcon />
                    </ScoreboardButton>
                </LinkContainer>
            )}
        </LinksContainer>
    );

    return (
        <>
            {game.specialEvent && <SpecialEventDisplay game={game} addMargin />}
            <GameOrSeriesScoreboard
                game={game}
                isShowingGameLinks={isShowingGameLinks}
                leftTeam={game.awayTeam}
                rightTeam={game.homeTeam}
            >
                <ScoreBox>
                    <ScoreContainer $isLosingScore={postGame && game.awayTeam.score < game.homeTeam.score}>
                        {game.awayTeam.score}
                    </ScoreContainer>
                    <PeriodTimeContainer>
                        <GameClock
                            game={game}
                            timezone={TimeZoneEnums.AMERICA_NEW_YORK}
                            flexDirection={GameClockStyles.FlexDirection.vertical}
                            intl={intl}
                        />
                        {!isLiveGame && <DateContainer>{formatDate(game.gameDate)}</DateContainer>}
                        {game.situation !== undefined && (
                            <SituationDescription>
                                <div>
                                    <FormattedMessage
                                        {...CommonMessages.gameSituationDescriptions.long.strength}
                                        values={{
                                            skaterCountA: Math.max(
                                                game.situation.awayTeam.strength,
                                                game.situation.homeTeam.strength
                                            ),
                                            skaterCountB: Math.min(
                                                game.situation.awayTeam.strength,
                                                game.situation.homeTeam.strength
                                            ),
                                        }}
                                    />
                                </div>
                                {game.situation.timeRemaining &&
                                    game.situation.awayTeam.strength === game.situation.homeTeam.strength && (
                                        <div>{game.situation.timeRemaining}</div>
                                    )}
                            </SituationDescription>
                        )}
                    </PeriodTimeContainer>
                    <ScoreContainer $isLosingScore={postGame && game.awayTeam.score > game.homeTeam.score}>
                        {game.homeTeam.score}
                    </ScoreContainer>
                </ScoreBox>
            </GameOrSeriesScoreboard>
            {isRadioPlayerOpen &&
                createPortal(
                    <RadioPlayer
                        homeTeam={game.homeTeam}
                        awayTeam={game.awayTeam}
                        closeRadioPlayer={() => setRadioPlayerOpen(false)}
                    />,
                    document.body
                )}
            {isShowingGameLinks && (
                <GameInfo>
                    <DateNetworkContainer
                        $showingBroadcasters={!CoreUtils.isPostGame(game.gameState) && game.tvBroadcasts?.length > 0}
                    >
                        {!CoreUtils.isPostGame(game.gameState) && (
                            <NetworkContainer>
                                <TvBroadcast
                                    tvBroadcasts={game.tvBroadcasts}
                                    location={countryCode}
                                    pageName={PageName.GameCenter}
                                />
                            </NetworkContainer>
                        )}
                    </DateNetworkContainer>
                    {gameLinks}
                </GameInfo>
            )}
            {isHowToWatchModalOpen && (
                <HowToWatchModal
                    howToWatch={howToWatchData}
                    isLoading={isHowToWatchDataLoading}
                    onCloseModal={() => setIsHowToWatchModalOpen(false)}
                />
            )}
        </>
    );
};

export default Scoreboard;
